import { AppApi, ScreenView, el } from './ui/app-api'
import { createFullscreenButton, type FullscreenButton } from './ui/FullscreenButton'
import { HUD } from './HUD'
import { PauseOverlay, GameOverOverlay, Banner, RunSummary } from './screens/Overlays'
import { createTitleScreen, createMainMenu } from './screens/MenuScreens'
import { createShopScreen } from './screens/ShopScreen'
import { createLeaderboardScreen } from './screens/LeaderboardScreen'
import { createSettingsScreen } from './screens/SettingsScreen'
import { CutsceneScreen } from './CutsceneScreen'
import { BuildVersionBadge } from './BuildVersionBadge'
import type { Cutscene } from '../data/cutscenes'
import type { Direction, HudState, ScreenName } from '../types'

/** Owns the entire DOM UI layer and screen routing. */
export class UIRoot {
  readonly hud: HUD
  readonly pause: PauseOverlay
  readonly gameOver: GameOverOverlay
  readonly banner: Banner
  readonly cutscene: CutsceneScreen
  readonly versionBadge: BuildVersionBadge
  /** Retained (not discarded) so its fullscreenchange listener can be torn down if UIRoot ever gains
   * a dispose path; today UIRoot is app-lifetime, so it simply lives for the page. */
  private readonly fullscreenBtn: FullscreenButton
  private screens: Record<ScreenName, ScreenView>
  private current: ScreenName | null = null
  private toastEl: HTMLElement
  private toastTimer: number | null = null
  private readonly mount: HTMLElement

  constructor(
    mount: HTMLElement,
    api: AppApi,
    onDir: (d: Direction) => void,
    onPause: () => void,
    onDevCompleteLevel?: () => void,
  ) {
    this.mount = mount
    this.hud = new HUD(onDir, onPause, onDevCompleteLevel)
    this.pause = new PauseOverlay(api)
    this.gameOver = new GameOverOverlay(api)
    this.banner = new Banner()
    this.cutscene = new CutsceneScreen()
    this.versionBadge = new BuildVersionBadge()
    this.toastEl = el('div', { class: 'toast' })

    this.screens = {
      title: createTitleScreen(api),
      menu: createMainMenu(api),
      shop: createShopScreen(api),
      leaderboard: createLeaderboardScreen(api),
      settings: createSettingsScreen(api),
      play: { el: el('div', { class: 'screen is-hidden' }) },
    }

    for (const name of Object.keys(this.screens) as ScreenName[]) {
      const view = this.screens[name]
      view.el.classList.add('is-hidden')
      mount.append(view.el)
    }
    mount.append(this.hud.el, this.pause.el, this.gameOver.el, this.banner.el, this.toastEl, this.versionBadge.el)
    // Persistent fullscreen toggle — pinned to a viewport corner above every screen/HUD.
    this.fullscreenBtn = createFullscreenButton(() => { api.audio.play('click'); api.toggleFullscreen() })
    mount.append(this.fullscreenBtn.el)
    mount.append(this.cutscene.el) // cutscene overlay sits on top of everything
  }

  /** Play a cutscene over the whole UI; `onDone` fires when it ends or is skipped. */
  playCutscene(cs: Cutscene, onDone: () => void): void {
    this.mount.classList.add('is-cutscene-active')
    this.cutscene.play(cs, () => {
      this.mount.classList.remove('is-cutscene-active')
      onDone()
    })
  }

  showScreen(name: ScreenName): void {
    if (this.current === name) return
    if (this.current) {
      const prev = this.screens[this.current]
      prev.el.classList.add('is-hidden')
      prev.onHide?.()
    }
    const next = this.screens[name]
    next.el.classList.remove('is-hidden')
    void next.onShow?.()
    this.current = name
    if (name === 'play') this.hud.show()
    else this.hud.hide()
  }

  /** Re-run the active screen's onShow (e.g. after async economy init resolves). */
  refreshCurrent(): void {
    if (this.current) void this.screens[this.current].onShow?.()
  }

  setHud(h: HudState): void {
    this.hud.update(h)
  }

  setDevCompleteLevelEnabled(enabled: boolean): void {
    this.hud.setDevCompleteLevelEnabled(enabled)
  }

  showPause(show: boolean): void {
    if (show) this.pause.show()
    else this.pause.hide()
  }

  showGameOver(summary: RunSummary): void {
    this.gameOver.show(summary)
  }
  hideGameOver(): void {
    this.gameOver.hide()
  }

  showBanner(text: string, durationMs = 0): void {
    this.banner.show(text, durationMs)
  }
  hideBanner(): void {
    this.banner.hide()
  }

  toast(msg: string): void {
    this.toastEl.textContent = msg
    this.toastEl.classList.add('is-show')
    if (this.toastTimer != null) clearTimeout(this.toastTimer)
    this.toastTimer = window.setTimeout(() => this.toastEl.classList.remove('is-show'), 1900)
  }
}
